import { useEffect, useRef, useState } from 'react';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { loadShare, type SharePayload } from '../data/shares.ts';
import { databaseBlocks, liveMarkdown, liveMarkdownTheme } from '../editor/liveMarkdown.ts';

/**
 * Die öffentliche Ansicht einer geteilten Notiz. Sie braucht kein Konto und keinen
 * entsperrten Vault — nur den Schlüssel, der hinter dem `#` in der Adresse steht und
 * den Server nie erreicht.
 */
export function Shared(): JSX.Element {
  const [payload, setPayload] = useState<SharePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = location.pathname.replace(/^\/s\//, '').replace(/\/$/, '');
    const key = new URLSearchParams(location.hash.replace(/^#/, '')).get('k');
    if (!id || !key) {
      setError('Dieser Link ist unvollständig — ohne den Teil hinter dem # lässt sich nichts öffnen.');
      return;
    }
    void loadShare(id, key)
      .then(setPayload)
      .catch((caught: Error) => {
        if (caught.message === 'expired') setError('Dieser Link ist abgelaufen.');
        else if (caught.message === 'not_found') setError('Diesen Link gibt es nicht mehr.');
        else setError('Der Inhalt ließ sich nicht entschlüsseln. Stimmt der Link vollständig?');
      });
  }, []);

  useEffect(() => {
    if (!payload || !host.current) return undefined;
    const view = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: payload.content,
        extensions: [
          EditorState.readOnly.of(true),
          EditorView.editable.of(false),
          markdown({ base: markdownLanguage }),
          liveMarkdown,
          databaseBlocks,
          liveMarkdownTheme,
          EditorView.lineWrapping,
        ],
      }),
    });
    return () => view.destroy();
  }, [payload]);

  if (error) {
    return (
      <div className="shared">
        <div className="shared-card">
          <p className="error">{error}</p>
        </div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="shared">
        <div className="shared-card">
          <p className="muted">wird entschlüsselt …</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shared">
      <article className="shared-card">
        <header className="shared-head">
          <span className="label">Geteilte Notiz</span>
          <span className="mono">
            Stand{' '}
            {new Date(payload.sharedAt).toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </header>
        <div className="shared-body" ref={host} />
        <footer className="shared-foot">
          Dieser Stand ist eingefroren. Spätere Änderungen erscheinen hier erst, wenn der
          Link aktualisiert wird.
        </footer>
      </article>
    </div>
  );
}
